/**
 * Landing Page Footer Component
 *
 * Professional footer with navigation links, contact info, and social media.
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Link } from 'react-router-dom';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';

import SecurityIcon from '@mui/icons-material/Security';
import SendIcon from '@mui/icons-material/Send';
import { landingColors } from '../../theme/landingTheme';
import Logo from './Logo';
import { supabase } from '../../services/supabaseService';
import { trackNewsletterSignup } from '../../utils/analytics';

// ============================================================================
// Types
// ============================================================================

interface FooterLink {
  label: string;
  path: string;
  external?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

// ============================================================================
// Footer Links Data
// ============================================================================

const footerSections: Record<string, FooterSection> = {
  produto: {
    title: 'Produto',
    links: [
      { label: 'Funcionalidades', path: '/funcionalidades' },
      { label: 'Planos e Preços', path: '/planos' },
      { label: 'Blog', path: '/blog' },
    ],
  },
  empresa: {
    title: 'Empresa',
    links: [
      { label: 'Sobre Nós', path: '/sobre' },
    ],
  },
  suporte: {
    title: 'Suporte',
    links: [
      { label: 'Central de Ajuda', path: '/contato' },
      { label: 'Contato', path: '/contato' },
      { label: 'WhatsApp', path: 'https://wa.me/5541996111900', external: true },
      { label: 'Portal do Paciente', path: '/portal/login' },
    ],
  },
};

const socialLinks = [
  { icon: <InstagramIcon />, url: 'https://instagram.com/ClinaFy', label: 'Instagram (abre em nova aba)' },
  { icon: <LinkedInIcon />, url: 'https://linkedin.com/company/clinafy', label: 'LinkedIn (abre em nova aba)' },
  { icon: <YouTubeIcon />, url: 'https://youtube.com/@clinafy', label: 'YouTube (abre em nova aba)' },
];

// ============================================================================
// Footer Component
// ============================================================================

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      setNewsletterError('Por favor, insira um email válido.');
      setNewsletterStatus('error');
      return;
    }

    setNewsletterStatus('loading');
    setNewsletterError('');

    try {
      const { error } = await supabase
        .from('leads')
        .insert({
          email: newsletterEmail,
          source: 'newsletter_footer',
          created_at: new Date().toISOString(),
        });

      if (error) {
        // Check if it's a duplicate email
        if (error.code === '23505') {
          setNewsletterError('Este email já está cadastrado!');
          setNewsletterStatus('error');
          trackNewsletterSignup(false);
        } else {
          throw error;
        }
      } else {
        setNewsletterStatus('success');
        setNewsletterEmail('');
        trackNewsletterSignup(true);
      }
    } catch (err) {
      console.error('Newsletter subscription error:', err);
      setNewsletterError('Erro ao cadastrar. Tente novamente.');
      setNewsletterStatus('error');
      trackNewsletterSignup(false);
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: landingColors.neutral.gray900,
        color: landingColors.neutral.gray300,
        pt: { xs: 6, md: 8 },
        pb: 4,
      }}
    >
      <Container maxWidth="xl">
        {/* Newsletter Section */}
        <Box
          sx={{
            bgcolor: 'rgba(27, 106, 99, 0.15)',
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            mb: 6,
            border: `1px solid ${landingColors.neutral.gray800}`,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  color: landingColors.neutral.white,
                  mb: 1,
                }}
              >
                Receba dicas para sua clínica
              </Typography>
              <Typography
                sx={{
                  color: landingColors.neutral.gray400,
                  fontSize: '0.95rem',
                }}
              >
                Conteúdo exclusivo sobre gestão de consultório, marketing para psicólogos e novidades da Clinafy.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {newsletterStatus === 'success' ? (
                <Alert
                  severity="success"
                  sx={{
                    bgcolor: 'rgba(46, 125, 50, 0.1)',
                    color: '#4caf50',
                    '& .MuiAlert-icon': { color: '#4caf50' },
                  }}
                >
                  Email cadastrado com sucesso! Em breve você receberá nossas dicas.
                </Alert>
              ) : (
                <Box
                  component="form"
                  onSubmit={handleNewsletterSubmit}
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Seu melhor email"
                    label="Email para newsletter"
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    error={newsletterStatus === 'error'}
                    helperText={newsletterStatus === 'error' ? newsletterError : ''}
                    sx={{
                      '& .MuiInputLabel-root': {
                        color: landingColors.neutral.gray500,
                        '&.Mui-focused': {
                          color: landingColors.primary.light,
                        },
                      },
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.05)',
                        borderRadius: 2,
                        '& fieldset': {
                          borderColor: landingColors.neutral.gray700,
                        },
                        '&:hover fieldset': {
                          borderColor: landingColors.neutral.gray600,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: landingColors.primary.main,
                        },
                      },
                      '& .MuiOutlinedInput-input': {
                        color: landingColors.neutral.white,
                        '&::placeholder': {
                          color: landingColors.neutral.gray500,
                          opacity: 1,
                        },
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#f44336',
                      },
                    }}
                    size="small"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={newsletterStatus === 'loading'}
                    endIcon={newsletterStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    sx={{
                      bgcolor: landingColors.accent.teal,
                      color: '#FFFFFF',
                      px: 3,
                      minWidth: { xs: '100%', sm: 140 },
                      '&:hover': {
                        bgcolor: '#1F8A7F',
                      },
                    }}
                  >
                    {newsletterStatus === 'loading' ? 'Enviando...' : 'Inscrever'}
                  </Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Main Footer Content */}
        <Grid container spacing={4}>
          {/* Brand Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Logo variant="light" size="lg" />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: landingColors.neutral.gray400,
                  maxWidth: 300,
                  lineHeight: 1.7,
                }}
              >
                A plataforma completa para profissionais de saúde mental que querem focar no que importa:
                seus pacientes. Prontuário eletrônico, agenda, teleconsulta e muito mais.
              </Typography>
            </Box>

            {/* Contact Info */}
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WhatsAppIcon sx={{ fontSize: 20, color: landingColors.accent.teal }} />
                <MuiLink
                  href="https://wa.me/5541996111900"
                  target="_blank"
                  rel="noopener"
                  aria-label="WhatsApp (41) 99611-1900 (abre em nova aba)"
                  sx={{
                    color: landingColors.neutral.gray300,
                    textDecoration: 'none',
                    '&:hover': { color: landingColors.neutral.white },
                  }}
                >
                  (41) 99611-1900
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon sx={{ fontSize: 20, color: landingColors.primary.light }} />
                <MuiLink
                  href="mailto:clinafy@gmail.com"
                  sx={{
                    color: landingColors.neutral.gray300,
                    textDecoration: 'none',
                    '&:hover': { color: landingColors.neutral.white },
                  }}
                >
                  clinafy@gmail.com
                </MuiLink>
              </Box>
            </Stack>

            {/* Social Links */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {socialLinks.map((social) => (
                <IconButton
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener"
                  aria-label={social.label}
                  sx={{
                    color: landingColors.neutral.gray400,
                    border: `1px solid ${landingColors.neutral.gray700}`,
                    '&:hover': {
                      color: landingColors.neutral.white,
                      borderColor: landingColors.primary.main,
                      bgcolor: 'rgba(27, 106, 99, 0.1)',
                    },
                  }}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>

          {/* Links Columns */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Grid container spacing={3}>
              {Object.entries(footerSections).map(([key, section]) => (
                <Grid key={key} size={{ xs: 6, sm: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      color: landingColors.neutral.white,
                      mb: 2,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '0.75rem',
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Stack spacing={1.5}>
                    {section.links.map((link) => (
                      <Box key={link.label}>
                        {link.external ? (
                          <MuiLink
                            href={link.path}
                            target="_blank"
                            rel="noopener"
                            sx={{
                              color: landingColors.neutral.gray400,
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              transition: 'color 0.2s ease',
                              '&:hover': {
                                color: landingColors.neutral.white,
                              },
                            }}
                          >
                            {link.label}
                          </MuiLink>
                        ) : (
                          <MuiLink
                            component={Link}
                            to={link.path}
                            sx={{
                              color: landingColors.neutral.gray400,
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              transition: 'color 0.2s ease',
                              '&:hover': {
                                color: landingColors.neutral.white,
                              },
                            }}
                          >
                            {link.label}
                          </MuiLink>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        {/* Security Badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mt: 6,
            mb: 4,
            py: 2,
            px: 3,
            bgcolor: 'rgba(27, 106, 99, 0.1)',
            borderRadius: 2,
            border: `1px solid ${landingColors.neutral.gray800}`,
          }}
        >
          <SecurityIcon sx={{ color: landingColors.accent.teal, fontSize: 24 }} />
          <Typography
            variant="body2"
            sx={{
              color: landingColors.neutral.gray300,
              fontWeight: 500,
            }}
          >
            Dados protegidos com criptografia AES-256 | Servidores AWS Brasil | Conformidade LGPD
          </Typography>
        </Box>

        <Divider sx={{ borderColor: landingColors.neutral.gray800, mb: 3 }} />

        {/* Bottom Bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: landingColors.neutral.gray500 }}
          >
            &copy; {currentYear} Clinafy. Todos os direitos reservados.
          </Typography>

        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
